import React, { useReducer, useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheet, View, Keyboard, Platform } from 'react-native';
import PropTypes from 'prop-types';
import { router, useLocalSearchParams } from 'expo-router';
import SubmitButton from '@/components/Buttons/SubmitButton';
import StatusContainer from '@/components/StatusContainer';
import Link from '@/components/Typography/Link';
import Text from '@/components/Typography/Text';
import { useAuthStore } from '@/store';
import { authApi } from '@/lib/api';
import { getClientInfo } from '@/constants/utils/analytics';
import { consumeCheckoutSignInReturn } from '@/lib/checkoutSignInReturn';
import { replaceAfterWebSignIn } from '@/lib/webSignInNavigation';
import FloatingLabelInput from '@/components/Inputs/FloatingLabelInput';
import Button from '@/components/Buttons/Button';
import OtpCodeInput from '@/components/Inputs/OtpCodeInput';
import useTranslation from '@/hooks/useTranslation';

const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: 400,
    gap: 10,
  },
  inputContainer: {
    gap: 8,
    marginBottom: 25,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  footer: {
    gap: 12,
    marginTop: 16,
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
  },
  linkContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
  },
});

const initialState = {
  token: '',
  error: '',
  isLoading: false,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_TOKEN':
      return { ...state, token: action.payload, error: '' };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'RESET':
      return { ...initialState };
    default:
      return state;
  }
};

const MfaForm = ({ ticket: ticketProp, email }) => {
  const { ticket: ticketFromParams } = useLocalSearchParams();
  const effectiveTicket = (ticketProp || ticketFromParams || '').toString();
  const setAuthData = useAuthStore(state => state.setAuthData);
  const { t } = useTranslation();

  const [method, setMethod] = useState('totp');
  const useBackupCode = method === 'backup';
  const [emailAvailable, setEmailAvailable] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const requestingEmail = useRef(false);

  useEffect(() => {
    let active = true;
    setEmailAvailable(false); setEmailSent(false); setMethod('totp'); setResendAt(0);
    dispatch({ type: 'RESET' });
    if (effectiveTicket) authApi.getMfaMethods(effectiveTicket).then(data => {
      if (active) setEmailAvailable(data?.emailAvailable === true);
    }).catch(() => {});
    return () => { active = false; };
  }, [effectiveTicket]);

  useEffect(() => {
    if (!resendAt) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [resendAt]);

  const selectMethod = value => {
    setMethod(value); dispatch({ type: 'SET_TOKEN', payload: '' });
  };
  const requestEmail = async () => {
    if (requestingEmail.current || submitting.current || Date.now() < resendAt) return;
    requestingEmail.current = true; setSendingEmail(true);
    dispatch({ type: 'SET_ERROR', payload: '' });
    try {
      const data = await authApi.requestMfaEmail(effectiveTicket);
      setEmailSent(true); selectMethod('email');
      setNow(Date.now()); setResendAt(Date.now() + (data.retryAfter || 60) * 1000);
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message || t('forms.mfa.emailFailed') });
    } finally { requestingEmail.current = false; setSendingEmail(false); }
  };
  const submitting = useRef(false);
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    return () => dispatch({ type: 'RESET' });
  }, []);

  const handleTokenChange = useCallback((text) => {
    // Normalize: trim, uppercase for potential backup code
    const normalized = text.replace(/\s+/g, '').toUpperCase();
    dispatch({ type: 'SET_TOKEN', payload: normalized });
  }, []);

  const handleSubmit = useCallback(async (codeOverride) => {
    if (submitting.current || requestingEmail.current) return;
    submitting.current = true;
    try {
      Keyboard.dismiss();

      const code = (codeOverride ?? state.token ?? '').trim();

      if (!effectiveTicket) {
        dispatch({ type: 'SET_ERROR', payload: t('forms.mfa.missingSession') });
        return;
      }
      if (!code) {
        dispatch({ type: 'SET_ERROR', payload: t('forms.mfa.enterCode') });
        return;
      }

      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: '' });

      let clientInfo;
      try {
        clientInfo = getClientInfo();
      } catch (_) {
        clientInfo = undefined;
      }
      const data = await authApi.loginMfa({ ticket: effectiveTicket, token: code, clientInfo, ...(method === 'email' && { method: 'email' }) });

      // If server indicates to return to login, do so immediately
      if (data?.returnToLogin) {
        router.replace('/login');
        return;
      }

      if (data.tokens?.access) {
        await setAuthData(data.tokens.access, data.tokens.expiresAt, data.tokens.refresh, data.user);
      }

      if (Platform.OS === 'web') replaceAfterWebSignIn(router, consumeCheckoutSignInReturn() || '/app');
      else router.replace('/(app)/app');
    } catch (error) {
      // If the API error response includes returnToLogin, redirect
      if (error?.data?.returnToLogin) {
        router.replace('/login');
        return;
      }
      dispatch({ type: 'SET_ERROR', payload: error.message || t('forms.mfa.verifyFailed') });
    } finally {
      submitting.current = false;
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [effectiveTicket, state.token, setAuthData, t, method]);

  return (
    <View style={styles.container}>
      <View style={styles.inputContainer}>
        <Text color="secondary">{t(method === 'email' ? 'forms.mfa.emailInstructions' : useBackupCode ? 'forms.mfa.backupInstructions' : 'forms.mfa.authenticatorInstructions')}</Text>
        {useBackupCode ? (
          <FloatingLabelInput
            label={t('forms.twoFactor.backupCodes')}
            value={state.token}
            onChangeText={handleTokenChange}
            placeholder="XXXX-XXXX"
            autoCapitalize="characters"
            autoCorrect={false}
            isDisabled={state.isLoading || sendingEmail}
            isFullWidth
          />
        ) : (
          <OtpCodeInput
            length={6}
            value={state.token}
            onChange={handleTokenChange}
            onComplete={(code) => {
              dispatch({ type: 'SET_TOKEN', payload: code });
              handleSubmit(code);
            }}
            autoFocus
            isDisabled={state.isLoading || sendingEmail}
            isError={!!state.error}
          />
        )}
        <Button
          text={t(method !== 'totp' ? 'forms.mfa.useAuthenticator' : 'forms.mfa.useBackupCode')}
          variant="secondary"
          isDisabled={state.isLoading || sendingEmail}
          onPress={() => selectMethod(method !== 'totp' ? 'totp' : 'backup')}
        />
        {method === 'email' && (
          <Button text={t('forms.mfa.useBackupCode')} variant="secondary"
            isDisabled={state.isLoading || sendingEmail} onPress={() => selectMethod('backup')} />
        )}
        {emailAvailable && (
          <Button
            text={t(method === 'email' ? 'forms.mfa.resendEmail' : emailSent ? 'forms.mfa.useEmail' : 'forms.mfa.sendEmail')}
            variant="secondary" isLoading={sendingEmail}
            isDisabled={state.isLoading || sendingEmail || (method === 'email' && now < resendAt)}
            onPress={() => emailSent && method !== 'email' ? selectMethod('email') : requestEmail()}
          />
        )}
      </View>

      {state.error ? (
        <StatusContainer type="error" message={state.error} />
      ) : null}
      
      <SubmitButton
        onPress={() => handleSubmit()}
        isLoading={state.isLoading || sendingEmail}
        title={t('forms.mfa.verifyAndLogin')}
      />

      <View style={styles.footer}>
        <View style={styles.linkContainer}>
          <Link href="/login">{t('forms.mfa.backToLogin')}</Link>
        </View>
      </View>
    </View>
  );
};

MfaForm.propTypes = {
  ticket: PropTypes.string,
  email: PropTypes.string,
};

export default React.memo(MfaForm);
